/**
 * UI slice — viewer display state + transient mesh generation UI.
 */
import type { StateCreator } from 'zustand';
import { DEFAULTS } from '../../constants';
import type { ViewerState, ViewerPatch } from '../schemas';
import type { ColorMode, QualityMetric, CadViewMode } from '../viewerEnums';
import { clamp01 } from '../../utils/clamp01';
import { showAppToast } from '../../lib/toasts';
import type { StoreState } from '../useStore';
import type { PreviewRepresentation } from '@/components/mesh-viewer/previewSource';
import type { ValidationItem } from '@/utils/meshValidation';
import {
  withOverlapPairChoice,
  type OverlapOutcomeStatus,
  type OverlapPairChoice,
  type OverlapPairChoices,
  type OverlapTreatmentOp,
} from '@/components/mesh/overlapBodies';
import * as chatPrefs from '@/utils/chatPrefs';

import {
  DEFAULT_CHAT_APPROVAL_MODE,
  type ChatApprovalMode,
} from '../chatApprovalMode';
// The contract with KhoriumAgents lives beside the code that writes it onto the
// wire; the store only holds one in flight.
import type {
  AgentChatRequest,
  AgentSessionRequest,
} from '@/components/assistant-ui/agentRunConfig';

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

export interface UiSlice extends ViewerState {
  // Viewer display
  //
  // The viewer display fields (cadViewMode, colorMode, qualityMetric, showAxes,
  // showHistogram, showWireframe, showSectionView, showClipPlane, sliceAxis,
  // slicePosition) are inherited from ViewerState (derived from viewerBase in
  // schemas.ts) so the store, agent action schema, and codegen all project off
  // one contract. Slice plane state (sliceAxis/slicePosition) is driven only by
  // the agent via setViewerSettings, which seeds the renderer one-way. User
  // interactions (X/Y/Z keys, slider drag) live inside the renderer and do NOT
  // write back here, so this can drift from what's actually displayed after
  // user-initiated changes. Per ENG-882 slice control is intentionally
  // open-loop: there is no renderer→store writeback, and buildAgentContext()
  // deliberately omits these fields rather than report a stale value to the
  // agent. slicePosition is normalized 0..1 along the chosen axis (0 = bbox min
  // side).
  //
  // MeshGen pre-mesh preview representation (bottom-left viewer switcher).
  // 'auto' follows the fine → diagnosis → CAD tessellation fallback chain;
  // the explicit values pin one source. Reset on project change.
  previewRepresentation: PreviewRepresentation;
  carveNameSelection: {
    geometryId: string;
    regionPrepAt: string | null;
    name: string;
  } | null;
  focusedWallFaces: number[];
  // Edge Load v1 viewer highlight: the currently-selected boundary edge's raw
  // Gmsh node-tag pairs (`BoundaryCurve.node_pairs`), written by
  // `StructuralBoundaryConditionsPanel` when a `load_edge` row's edge picker
  // has a selection. `null` = no edge highlighted. Separate from
  // `focusedWallFaces` (face-index-keyed) since edges resolve through the
  // renderer's own node buffer, not the owner-tag/highlight-range system.
  focusedEdgeSegments: Array<[number, number]> | null;
  // Point Load (load_patch) click-to-place: the face label whose patch centre
  // the next viewer click should SET, instead of the click toggling face
  // selection as it normally does. Written by
  // `StructuralBoundaryConditionsPanel` when a patch row's "place" affordance
  // is engaged; read by the viewer's pick handler, which routes the click to
  // `armPatchPlacement`'s consumer and disarms. `null` = clicks behave
  // normally.
  //
  // In the store rather than panel state for the same reason
  // `selectedBcPatches` is: the two halves of the interaction live in
  // different component trees (the BC panel and the viewer are siblings
  // communicating only through here), and a click has to be able to find the
  // arming without a prop path between them.
  // Carries the TARGET, not just the face: `moveKey` null means the next
  // click ADDS a patch to this face, a key means it RELOCATES that one. A face
  // can hold several point loads now, so "this face's patch" is no longer a
  // unique thing to place onto.
  armedPatch: {
    label: string;
    moveKey: string | null;
    snapMode: 'node' | 'face' | 'facet';
  } | null;
  thermalNodePickerArmed: boolean;
  thermalHoveredNode: { id: number; point: [number, number, number] } | null;
  thermalPickedNode: { id: number; point: [number, number, number] } | null;
  /**
   * World positions for authored `heat_point` node ids. The wire BC only
   * carries `node_id`, so the draft remembers the pick location to keep the
   * mesh marker after Add / across hover.
   */
  thermalPointAnchors: Record<number, [number, number, number]>;
  /** Sidebar hover focus for one heat-point marker (node id). */
  focusedThermalPointId: number | null;
  // The world-space point the last armed click landed on, in mesh model space
  // (mm). Written by the viewer, consumed and cleared by the panel, which
  // turns it into the `center_mm` of a `load_patch` BC. A one-shot handoff
  // rather than durable state: the committed centre lives in the BC itself.
  placedPatchPoint: {
    label: string;
    moveKey: string | null;
    snapMode?: 'node' | 'face' | 'facet';
    point: [number, number, number];
  } | null;
  highlightedEnclosureFaces: Set<string>;
  hoveredEnclosureFace: string | null;
  // Local Refinement panel ↔ viewer interplay: selection drives which zone the
  // overlay emphasizes (and shows corner handles for), hover (panel row) tints
  // the zone in the viewer.
  selectedRefinementZoneId: string | null;
  hoveredRefinementZoneId: string | null;
  // Box-definition edit mode for the Local Refinement panel. Lives here (not
  // panel-local) so the viewer overlay can emphasize the min/max corner
  // vertices only while the user is editing them in min/max mode.
  refinementBoxEditMode: 'centerSize' | 'minMax';
  // Visibility-only toggle for the enclosure preview in SimOps viewers.
  // Default false because the wind-tunnel box dominates the canvas and
  // obscures the actual geometry users are picking BCs for. Mirrors
  // MeshGen's Eye/EyeOff toggle on the Enclosure section, but lives on
  // a separate field so toggling visibility doesn't flip MeshGen-side
  // `enclosureEnabled` (which is the meshing-config gate).
  simopsEnclosureVisible: boolean;
  // Eye/hide toggle for the internal-flow seed-point marker (amber dot) in the
  // Boundary Assignment panel — mirrors the enclosure Eye toggle. Default true
  // so the seed (user's, or the auto default) is visible while editing.
  seedPointVisible: boolean;
  openingLocate: {
    geometryId: string;
    // Ordered rim coordinates preview the cap (ENG-2467); legacy mouths use
    // centroid/normal/radius. A dot stands in when no cap geometry is available
    // or the mouth is not capped (the user marked it "not an opening").
    centroid: [number, number, number];
    normal: [number, number, number];
    radius: number | null;
    rimPoints?: [number, number, number][];
    capped: boolean;
  } | null;
  // All face_indices currently being "soft-highlighted" (citrine paint).
  // Stays an array so multi-face BC rows (e.g. an internal-flow `wall`
  // patch with N faces) can paint every face on hover, not just the first.
  // setHoveredWallFace(single) is kept as a convenience wrapper for the
  // existing MeshGen / BL panels that only deal with one face at a time.
  hoveredWallFaces: number[];
  /**
   * Faces whose highlight should be drawn through the geometry in front of
   * it. Written by the fastener section, whose faces sit inside the joint and
   * are therefore invisible to the depth-tested highlight pass (ENG-2404).
   */
  seeThroughWallFaces: number[];
  // SimOps Boundary-Conditions multi-selection: canonical polyMesh patch names
  // currently selected for bulk role reassignment. In the store (not panel
  // state) so the 3D viewer can drive it — a viewer pick toggles a patch here
  // and both the BC panel and the highlight react. The panel derives the
  // persistent highlight (focusedWallFaces / enclosure paint) from this set as
  // the single writer, so panel and viewer can't fight over the highlight.
  selectedBcPatches: string[];
  // BC role groups (inlet/outlet/farfield/symmetry/wall) the user has hidden
  // via the per-group eye toggle — the viewer omits their painted faces + flow
  // arrows. Values are BCRoleGroup strings; empty = all groups visible.
  hiddenBcGroups: string[];
  // BL Coverage row click-to-focus: persists the highlight on a chosen
  // patch's face until the user clicks elsewhere. Distinct from
  // `focusedWallFaces` (BL setup Ctrl-click picker, painted amethyst):
  // this one is post-meshing, paints in the row's coverage tone
  // (red/amber/blue/green) so the focus color carries the same
  // semantic as the row badge. `null` when no row is focused.
  // `faceIndex` is `mesh_entity_tag - 1` (BE-resolved polyMesh patch
  // index + 1 per `meshgen-face-tag-polymesh-remap`; see
  // `FaceTagAssignment.mesh_entity_tag`) — aligns with the WASM
  // parser's owner data so the lookup works uniformly for internal
  // flow, external flow, and CHT without per-mode translation.
  coverageFocus: {
    faceIndex: number;
    tone: 'destructive' | 'warning' | 'info' | 'success';
  } | null;
  // Transient highlight map keyed by override-field name (or 'initial_height')
  // pointing at the direction the field just moved. Consumed by the override /
  // BL inputs to flash green (good direction) or red (bad). null = nothing
  // recently flashed. Cleared after a short timeout by the writer.
  flashedFields: Record<string, 'up' | 'down'> | null;
  // Live mesh-param validation items, published by `MeshValidationPanel`'s
  // hook so individual inputs (OverrideInput etc.) can render an inline
  // warning indicator next to their label without re-running the validator
  // per input. The validator already memoizes `items` so writing on every
  // render is a Zustand no-op when nothing changed.
  meshValidationItems: ValidationItem[];

  // Diagnosis layer visibility (Geometry Health "eye" toggles). A layer id in
  // this set is HIDDEN in the viewer; empty set = every layer visible. Kept as
  // a hidden-set (not a visible-set) so the slice needn't know the full layer
  // list — the panel materializes ids from the diagnosis result.
  hiddenDiagnosisLayers: Set<string>;
  // Diagnosis layer currently hovered in the Geometry Health panel. The viewer
  // emphasizes it and dims the rest; null = no emphasis.
  hoveredDiagnosisLayer: string | null;
  // Which pair of overlapping bodies the volume-overlap finding is focused on
  // (ENG-2470), as an `overlapPairKey` — "2:10", never a list position, for
  // the reason `hoveredStructuralBody` gives below. Hover is what the cursor
  // is over right now; pinned survives the cursor leaving the rail, so a
  // reader can point at one pair and then go orbit the model. The viewer dims
  // the finding's other overlaps rather than hiding them, so the pair reads as
  // part of the set it came from.
  hoveredOverlapPair: string | null;
  pinnedOverlapPair: string | null;
  // Isolate, narrowed from the layer to the pinned pair: the viewer drops the
  // finding's other overlaps instead of dimming them. Meaningless without a
  // pin, so unpinning clears it in the same write.
  overlapPairIsolated: boolean;
  // What the user has decided about each pair (ENG-2501): ticked or not, the
  // op, and for a cut which body loses the lens — keyed by `overlapPairKey`,
  // absent fields meaning "the default" (see `OverlapPairChoice`). In the
  // store rather than the rail because two other surfaces read it: the
  // viewer colours the active pair by its op (loser red, winner green), and
  // the inspector strip counts the ticks for its own Treat button. Cleared
  // when the displayed version changes — two versions of one assembly share
  // pair keys, and a pair ticked on v2 must not arrive ticked on v3.
  overlapPairChoices: OverlapPairChoices;
  // The approval panel's hovered per-pair outcome (ENG-2501), painted on the
  // viewer in its status tone. Only meaningful while the viewer shows the
  // SOURCE version: the outcomes name source body indices, and a fragment
  // renumbers most of the candidate's. The panel only writes it then.
  hoveredOverlapOutcome: { key: string; status: OverlapOutcomeStatus } | null;
  // Geometry-version id hovered in the Diagnosis Files tree / Repair History.
  // Both surfaces highlight the matching row so hover stays in sync across them.
  hoveredVersionId: string | null;

  // FEA body panel (ENG-2294). Three separate pieces of state because they
  // answer three different questions and a reader needs all three at once:
  // which body is under the cursor RIGHT NOW, which one they clicked and want
  // to keep marked, and which ones they have hidden to see past.
  //
  // `body_index`, never a list position — a skipped degenerate volume
  // shortens the descriptor list without renumbering the survivors
  // (ENG-1610), so a position would point at the wrong body.
  //
  // Hover is written by BOTH the panel row and the viewer, which is what makes
  // the highlight two-way: whichever surface the cursor is over, the other
  // one lights up. Same shape as `hoveredVersionId` above, for the same
  // reason.
  hoveredStructuralBody: number | null;
  // A click, held until focus moves elsewhere in the panel. Distinct from
  // hover because on six near-identical rivets "the one I am pointing at" and
  // "the one I chose" have to be tellable apart.
  selectedStructuralBody: number | null;
  // Hidden-set rather than visible-set, matching `hiddenDiagnosisLayers`: the
  // slice needn't know how many bodies exist, and a geometry that gains one
  // shows it rather than hiding it by omission.
  hiddenStructuralBodies: Set<number>;

  // CAD health UI
  showCleaner: boolean;
  healMeshLoading: boolean;
  isAnalyzing: boolean;

  // Sidebar panels
  leftSidebarOpen: boolean;
  chatOpen: boolean;
  chatApprovalMode: ChatApprovalMode;
  cadSectionOpen: boolean;
  meshSectionOpen: boolean;
  vizSectionOpen: boolean;
  consoleOpen: boolean;
  // SimOps Analyze writeup drawer — lifted here so the merged study header's
  // Writeup button (ENG-1842) and EvaluationWorkbench's own rail toggle drive
  // one canonical field. ENG-1844 will replace the rail's ResizablePanel with
  // a right-side Sheet reading this same field.
  simopsWriteupOpen: boolean;

  // Preview
  previewAttemptedForProjectId: string | null;
  isLoadingPreview: boolean;

  // Ansys dropdown
  isAnsysDropdownOpen: boolean;

  // Batch
  mode: string;
  batchFiles: unknown[];
  selectedBatchFileId: string | null;

  // Session restore
  isRestoringSession: boolean;
  sessionRestoreCancelled: boolean;
  restoredCameraPosition: { x: number; y: number; z: number } | null;
  restoredCameraTarget: { x: number; y: number; z: number } | null;
  // Geometry id whose authoring state (toolSlice) has finished hydrating in
  // `useInitialProjectLoad` — i.e. mesh-record-derived values + the persisted
  // per-geometry draft have both been applied (ENG-841). The MeshGen authoring
  // panel gates its mount on this matching `currentProject` so it seeds its
  // provider from the fully-restored store rather than a pre-hydration
  // snapshot (the object fields like domainParams aren't bridged post-mount).
  // null = no project hydrated yet / hydration in progress.
  authoringHydratedFor: string | null;

  // Upload lifecycle UI (polling/progress in useGeometryStatus)
  isUploading: boolean;
  uploadProgress: number;
  isExportingAnsys: boolean;

  // Actions — viewer
  showToast: (message: string) => void;
  setCadViewMode: (v: CadViewMode) => void;
  setPreviewRepresentation: (v: UiSlice['previewRepresentation']) => void;
  setCarveNameSelection: (v: UiSlice['carveNameSelection']) => void;
  setColorMode: (v: ColorMode) => void;
  setQualityMetric: (v: QualityMetric) => void;
  setShowAxes: (v: boolean) => void;
  setShowHistogram: (v: boolean) => void;
  setShowWireframe: (v: boolean) => void;
  setShowSectionView: (v: boolean) => void;
  setShowClipPlane: (v: boolean) => void;
  // No individual setters for sliceAxis/slicePosition — the agent uses the
  // aggregate setViewerSettings, and user interactions live inside the
  // renderer (open-loop, no writeback; see ENG-882).
  setFocusedWallFaces: (faces: number[]) => void;
  setSeeThroughWallFaces: (faces: number[]) => void;
  setFocusedEdgeSegments: (segments: Array<[number, number]> | null) => void;
  /**
   * Arm (or disarm with null) click-to-place. `moveKey` null arms a NEW patch
   * on that face; a key arms a relocation of that specific patch.
   */
  armPatchPlacement: (
    armed: {
      label: string;
      moveKey: string | null;
      snapMode: 'node' | 'face' | 'facet';
    } | null,
  ) => void;
  setThermalNodePickerArmed: (armed: boolean) => void;
  setThermalHoveredNode: (node: UiSlice['thermalHoveredNode']) => void;
  setThermalPickedNode: (node: UiSlice['thermalPickedNode']) => void;
  rememberThermalPointAnchor: (
    nodeId: number,
    point: [number, number, number],
  ) => void;
  forgetThermalPointAnchor: (nodeId: number) => void;
  setFocusedThermalPointId: (nodeId: number | null) => void;
  /** Viewer -> panel handoff of a placed point; also disarms. */
  setPlacedPatchPoint: (
    placed: {
      label: string;
      moveKey: string | null;
      snapMode?: 'node' | 'face' | 'facet';
      point: [number, number, number];
    } | null,
  ) => void;
  toggleHighlightedEnclosureFace: (face: string) => void;
  setHighlightedEnclosureFaces: (faces: string[]) => void;
  clearHighlightedEnclosureFaces: () => void;
  setHoveredEnclosureFace: (face: string | null) => void;
  setSelectedRefinementZoneId: (id: string | null) => void;
  setHoveredRefinementZoneId: (id: string | null) => void;
  setRefinementBoxEditMode: (v: 'centerSize' | 'minMax') => void;
  setHoveredWallFace: (face: number | null) => void;
  setCoverageFocus: (focus: UiSlice['coverageFocus']) => void;
  setHoveredWallFaces: (faces: number[]) => void;
  setSelectedBcPatches: (labels: string[]) => void;
  toggleBcPatch: (label: string) => void;
  clearBcSelection: () => void;
  toggleBcGroup: (group: string) => void;
  clearBcGroupVisibility: () => void;
  setSimopsEnclosureVisible: (v: boolean) => void;
  setSeedPointVisible: (v: boolean) => void;
  setOpeningLocate: (value: UiSlice['openingLocate']) => void;
  setFlashedFields: (v: Record<string, 'up' | 'down'> | null) => void;
  setMeshValidationItems: (items: ValidationItem[]) => void;
  toggleDiagnosisLayer: (id: string) => void;
  showAllDiagnosisLayers: () => void;
  // Set the hidden-layer set outright. Used by the standalone Diagnosis page's
  // "Isolate" action to show only one layer (ENG-846); the eye toggles and
  // `showAllDiagnosisLayers` keep operating on the same set.
  setHiddenDiagnosisLayers: (ids: string[]) => void;
  setHoveredDiagnosisLayer: (id: string | null) => void;
  setHoveredOverlapPair: (key: string | null) => void;
  /** Pin a pair, or unpin it when it is already the pinned one. */
  togglePinnedOverlapPair: (key: string) => void;
  setOverlapPairIsolated: (v: boolean) => void;
  /** Tick or untick a pair for the batch. */
  setOverlapPairSelected: (key: string, selected: boolean) => void;
  /** Choose a pair's op; the loser is kept, and only travels with a cut. */
  setOverlapPairOp: (key: string, op: OverlapTreatmentOp) => void;
  /** Swap which body a cut takes the lens from. */
  setOverlapPairCutLoser: (key: string, bodyIndex: number) => void;
  /** Forget every choice and any outcome hover — the displayed version changed. */
  clearOverlapPairChoices: () => void;
  /**
   * The approval panel's hovered outcome — and, in the same write, the
   * hovered pair it names (`v?.key ?? null`), so the panel's enter, leave and
   * cleanup are each one store write and the two fields cannot drift apart.
   */
  setHoveredOverlapOutcome: (
    v: { key: string; status: OverlapOutcomeStatus } | null,
  ) => void;
  setHoveredVersionId: (id: string | null) => void;
  setHoveredStructuralBody: (v: number | null) => void;
  setSelectedStructuralBody: (v: number | null) => void;
  toggleStructuralBodyHidden: (bodyIndex: number) => void;
  showAllStructuralBodies: () => void;

  setShowCleaner: (v: boolean) => void;
  setHealMeshLoading: (v: boolean) => void;
  setIsAnalyzing: (v: boolean) => void;

  setLeftSidebarOpen: (v: boolean) => void;
  setChatOpen: (v: boolean) => void;
  setChatApprovalMode: (mode: ChatApprovalMode) => void;
  setCadSectionOpen: (v: boolean) => void;
  setMeshSectionOpen: (v: boolean) => void;
  setVizSectionOpen: (v: boolean) => void;
  setConsoleOpen: (v: boolean) => void;
  setSimopsWriteupOpen: (v: boolean) => void;

  setPreviewAttemptedForProjectId: (v: string | null) => void;
  setIsLoadingPreview: (v: boolean) => void;

  setIsAnsysDropdownOpen: (v: boolean) => void;

  setMode: (v: string) => void;
  setBatchFiles: (v: unknown[]) => void;
  setSelectedBatchFileId: (v: string | null) => void;

  setIsRestoringSession: (v: boolean) => void;
  cancelSessionRestore: () => void;
  setRestoredCameraPosition: (
    v: { x: number; y: number; z: number } | null,
  ) => void;
  setRestoredCameraTarget: (
    v: { x: number; y: number; z: number } | null,
  ) => void;
  setAuthoringHydratedFor: (v: string | null) => void;

  // Actions — upload and export UI
  setIsUploading: (v: boolean) => void;
  setUploadProgress: (v: number) => void;
  setIsExportingAnsys: (v: boolean) => void;

  // Agent chat trigger (one-shot request from a surface outside the sidebar)
  agentChatRequest: AgentChatRequest | null;
  requestAgentChat: (request: AgentChatRequest | null) => void;

  // Open an EXISTING agent session — the retry advisor's own transcript,
  // reached from the decision record that names it. `scope` is the session
  // list tab the sidebar must switch to; the CALLER knows which list its
  // session lives on, and the sidebar cannot derive it (the messages endpoint
  // carries no `origin`), so passing it beats hardcoding one.
  //
  // Separate from `agentChatRequest` because the two consumers do genuinely
  // different things at different points in the sidebar and share only a few
  // lines of preamble — NOT because a single discriminated field would be
  // unsafe. It would not: a `{kind:'existing'}` arm carries no `prompt`, so a
  // caller could no more attach one than it can today. The invariant that
  // actually keeps a prompt out of a loaded transcript is `pendingAppendRef`'s
  // `forVersion` stamp in the sidebar, not the shape of this field.
  //
  // What two fields DO cost is that both can be set at once, which a union
  // makes unrepresentable — so the setters below enforce it instead: each
  // clears the other, last click wins. Without that, a reader who clicks "Ask
  // advisor" then "View transcript" during a session restore parks both, and
  // on settle the chat effect starts a run that the transcript load then
  // remounts out from under.
  agentSessionRequest: AgentSessionRequest | null;
  requestAgentSession: (request: AgentSessionRequest | null) => void;

  // Aggregate setter (for agent use)
  setViewerSettings: (patch: ViewerPatch) => void;

  resetViewerState: () => void;
  resetJobState: () => void;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

// Shared empty ref + content-equality guard so clearing or re-setting wall
// faces doesn't fabricate new array identities — subscribers (viewer highlight
// rebuild via useFaceHighlights, the boundary panel's focusedFaceSet) memo on
// array identity, and callers pass fresh `[]` literals per event (ENG-821).
const NO_WALL_FACES: number[] = [];
const sameFaces = (a: number[], b: number[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

// The 10 viewer display defaults, fully checked against ViewerState — every
// field present and every literal a valid enum member (a missing field or an
// invalid enum literal fails tsc here, keeping the store defaults in lockstep
// with viewerBase in schemas.ts).
const viewerDefaults = {
  cadViewMode: 'surface',
  colorMode: 'solid',
  qualityMetric: 'sicn',
  showAxes: true,
  showHistogram: false,
  showWireframe: false,
  showSectionView: false,
  showClipPlane: false,
  sliceAxis: 'x',
  slicePosition: 0.5,
} satisfies ViewerState;

const viewerInitialState = {
  ...viewerDefaults,
  previewRepresentation: 'auto' as const,
  carveNameSelection: null,
  focusedWallFaces: [] as number[],
  focusedEdgeSegments: null as Array<[number, number]> | null,
  highlightedEnclosureFaces: new Set<string>(),
  hoveredEnclosureFace: null as string | null,
  selectedRefinementZoneId: null as string | null,
  hoveredRefinementZoneId: null as string | null,
  refinementBoxEditMode: 'centerSize' as 'centerSize' | 'minMax',
  hoveredWallFaces: [] as number[],
  seeThroughWallFaces: [] as number[],
  selectedBcPatches: [] as string[],
  armedPatch: null as {
    label: string;
    moveKey: string | null;
    snapMode: 'node' | 'face' | 'facet';
  } | null,
  thermalNodePickerArmed: false,
  thermalHoveredNode: null,
  thermalPickedNode: null,
  thermalPointAnchors: {} as Record<number, [number, number, number]>,
  focusedThermalPointId: null as number | null,
  placedPatchPoint: null as {
    label: string;
    moveKey: string | null;
    snapMode?: 'node' | 'face' | 'facet';
    point: [number, number, number];
  } | null,
  hiddenBcGroups: [] as string[],
  coverageFocus: null as UiSlice['coverageFocus'],
  simopsEnclosureVisible: false,
  seedPointVisible: true,
  openingLocate: null,
  flashedFields: null as Record<string, 'up' | 'down'> | null,
  meshValidationItems: [] as ValidationItem[],
  hiddenDiagnosisLayers: new Set<string>(),
  hoveredDiagnosisLayer: null as string | null,
  hoveredStructuralBody: null as number | null,
  selectedStructuralBody: null as number | null,
  hiddenStructuralBodies: new Set<number>(),
  hoveredOverlapPair: null as string | null,
  pinnedOverlapPair: null as string | null,
  overlapPairIsolated: false,
  overlapPairChoices: new Map<
    string,
    OverlapPairChoice
  >() as OverlapPairChoices,
  hoveredOverlapOutcome: null as UiSlice['hoveredOverlapOutcome'],
  hoveredVersionId: null as string | null,

  showCleaner: false,
  healMeshLoading: false,
  isAnalyzing: false,

  leftSidebarOpen: true,
  chatOpen: chatPrefs.getOpen(),
  chatApprovalMode: DEFAULT_CHAT_APPROVAL_MODE,
  cadSectionOpen: true,
  meshSectionOpen: true,
  vizSectionOpen: true,
  consoleOpen: DEFAULTS.CONSOLE_OPEN,
  simopsWriteupOpen: true,

  previewAttemptedForProjectId: null,
  isLoadingPreview: false,

  isAnsysDropdownOpen: false,

  mode: 'single' as string,
  batchFiles: [] as unknown[],
  selectedBatchFileId: null as string | null,

  isRestoringSession: true,
  sessionRestoreCancelled: false,
  restoredCameraPosition: null as { x: number; y: number; z: number } | null,
  restoredCameraTarget: null as { x: number; y: number; z: number } | null,
  authoringHydratedFor: null as string | null,

  agentChatRequest: null as AgentChatRequest | null,
  agentSessionRequest: null as AgentSessionRequest | null,
};

const jobInitialState = {
  isUploading: false,
  uploadProgress: 0,
  isExportingAnsys: false,
};

const uiInitialState = { ...viewerInitialState, ...jobInitialState };

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

function sameOpeningLocate(
  a: UiSlice['openingLocate'],
  b: UiSlice['openingLocate'],
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aRim = a.rimPoints ?? [];
  const bRim = b.rimPoints ?? [];
  return (
    a.geometryId === b.geometryId &&
    a.radius === b.radius &&
    a.capped === b.capped &&
    sameFaces(a.centroid, b.centroid) &&
    sameFaces(a.normal, b.normal) &&
    aRim.length === bRim.length &&
    aRim.every((point, index) => sameFaces(point, bRim[index]))
  );
}

export const createUiSlice: StateCreator<
  StoreState,
  [['zustand/devtools', never], ['zustand/subscribeWithSelector', never]],
  [],
  UiSlice
> = (set) => {
  return {
    ...uiInitialState,

    // Viewer display
    setCadViewMode: (v) => set({ cadViewMode: v }),
    setPreviewRepresentation: (v) => set({ previewRepresentation: v }),
    setCarveNameSelection: (v) => set({ carveNameSelection: v }),
    setColorMode: (v) => set({ colorMode: v }),
    setQualityMetric: (v) => set({ qualityMetric: v }),
    setShowAxes: (v) => set({ showAxes: v }),
    setShowHistogram: (v) => set({ showHistogram: v }),
    setShowWireframe: (v) => set({ showWireframe: v }),
    setShowSectionView: (v) => set({ showSectionView: v }),
    setShowClipPlane: (v) => set({ showClipPlane: v }),
    setFocusedWallFaces: (faces) =>
      set((s) =>
        sameFaces(s.focusedWallFaces, faces)
          ? s
          : { focusedWallFaces: faces.length === 0 ? NO_WALL_FACES : faces },
      ),
    setFocusedEdgeSegments: (segments) =>
      set({ focusedEdgeSegments: segments }),
    armPatchPlacement: (armed) =>
      // Clears any stale handoff alongside the arm: a point placed for a
      // previous row must not be read as this row's answer.
      set({ armedPatch: armed, placedPatchPoint: null }),
    setThermalNodePickerArmed: (armed) =>
      set({
        thermalNodePickerArmed: armed,
        thermalHoveredNode: null,
        thermalPickedNode: null,
      }),
    setThermalHoveredNode: (node) =>
      set((state) => {
        const current = state.thermalHoveredNode;
        if (current === node || (!current && !node)) return state;
        if (
          current &&
          node &&
          current.id === node.id &&
          current.point.every(
            (coordinate, axis) => coordinate === node.point[axis],
          )
        )
          return state;
        return { thermalHoveredNode: node };
      }),
    setThermalPickedNode: (node) =>
      set({
        thermalPickedNode: node,
        thermalNodePickerArmed: false,
        thermalHoveredNode: null,
      }),
    rememberThermalPointAnchor: (nodeId, point) =>
      set((state) => ({
        thermalPointAnchors: {
          ...state.thermalPointAnchors,
          [nodeId]: point,
        },
      })),
    forgetThermalPointAnchor: (nodeId) =>
      set((state) => {
        if (!(nodeId in state.thermalPointAnchors)) return state;
        const { [nodeId]: _removed, ...rest } = state.thermalPointAnchors;
        return {
          thermalPointAnchors: rest,
          focusedThermalPointId:
            state.focusedThermalPointId === nodeId
              ? null
              : state.focusedThermalPointId,
        };
      }),
    setFocusedThermalPointId: (nodeId) =>
      set({ focusedThermalPointId: nodeId }),
    setPlacedPatchPoint: (placed) =>
      // Disarms on placement — one click, one point. Re-arming is an explicit
      // act, so a stray second click cannot silently move a placed centre.
      set({ placedPatchPoint: placed, armedPatch: null }),
    toggleHighlightedEnclosureFace: (face) =>
      set((s) => {
        const next = new Set(s.highlightedEnclosureFaces);
        if (next.has(face)) next.delete(face);
        else next.add(face);
        return { highlightedEnclosureFaces: next };
      }),
    setHighlightedEnclosureFaces: (faces) =>
      set((s) => {
        if (
          faces.length === s.highlightedEnclosureFaces.size &&
          faces.every((f) => s.highlightedEnclosureFaces.has(f))
        )
          return s;
        return { highlightedEnclosureFaces: new Set(faces) };
      }),
    clearHighlightedEnclosureFaces: () =>
      set((s) =>
        s.highlightedEnclosureFaces.size === 0
          ? s
          : { highlightedEnclosureFaces: new Set() },
      ),
    setHoveredEnclosureFace: (face) => set({ hoveredEnclosureFace: face }),
    setSelectedRefinementZoneId: (id) => set({ selectedRefinementZoneId: id }),
    setHoveredRefinementZoneId: (id) => set({ hoveredRefinementZoneId: id }),
    setRefinementBoxEditMode: (v) => set({ refinementBoxEditMode: v }),
    setHoveredWallFace: (face) =>
      set((s) => {
        const next = face == null ? NO_WALL_FACES : [face];
        return sameFaces(s.hoveredWallFaces, next)
          ? s
          : { hoveredWallFaces: next };
      }),
    setSeeThroughWallFaces: (faces) =>
      set((s) =>
        sameFaces(s.seeThroughWallFaces, faces)
          ? s
          : { seeThroughWallFaces: faces.length === 0 ? NO_WALL_FACES : faces },
      ),
    setHoveredWallFaces: (faces) =>
      set((s) =>
        sameFaces(s.hoveredWallFaces, faces)
          ? s
          : { hoveredWallFaces: faces.length === 0 ? NO_WALL_FACES : faces },
      ),
    setSelectedBcPatches: (labels) =>
      // Identity guard (mirrors the sameFaces guard on the wall-face setters)
      // so a no-op re-set (e.g. select-all on an already-full group) doesn't
      // re-render the viewer + re-run the highlight derivation.
      set((s) =>
        s.selectedBcPatches.length === labels.length &&
        s.selectedBcPatches.every((l, i) => l === labels[i])
          ? s
          : { selectedBcPatches: labels },
      ),
    toggleBcPatch: (label) =>
      set((s) => ({
        selectedBcPatches: s.selectedBcPatches.includes(label)
          ? s.selectedBcPatches.filter((l) => l !== label)
          : [...s.selectedBcPatches, label],
      })),
    clearBcSelection: () =>
      set((s) =>
        s.selectedBcPatches.length === 0 ? s : { selectedBcPatches: [] },
      ),
    toggleBcGroup: (group) =>
      set((s) => ({
        hiddenBcGroups: s.hiddenBcGroups.includes(group)
          ? s.hiddenBcGroups.filter((g) => g !== group)
          : [...s.hiddenBcGroups, group],
      })),
    clearBcGroupVisibility: () =>
      set((s) => (s.hiddenBcGroups.length === 0 ? s : { hiddenBcGroups: [] })),
    setCoverageFocus: (focus) => set({ coverageFocus: focus }),
    setSimopsEnclosureVisible: (v) => set({ simopsEnclosureVisible: v }),
    setSeedPointVisible: (v) => set({ seedPointVisible: v }),
    // Same value again (a row's mouseenter then focus) is a no-op, so the
    // viewer does not rebuild the disc and its GPU buffer for nothing.
    setOpeningLocate: (openingLocate) =>
      set((s) =>
        sameOpeningLocate(s.openingLocate, openingLocate)
          ? s
          : { openingLocate },
      ),
    setFlashedFields: (v) => set({ flashedFields: v }),
    setMeshValidationItems: (items) => set({ meshValidationItems: items }),
    setHoveredStructuralBody: (v) => set({ hoveredStructuralBody: v }),
    setSelectedStructuralBody: (v) => set({ selectedStructuralBody: v }),
    toggleStructuralBodyHidden: (bodyIndex) =>
      set((s) => {
        const next = new Set(s.hiddenStructuralBodies);
        if (next.has(bodyIndex)) next.delete(bodyIndex);
        else next.add(bodyIndex);
        return { hiddenStructuralBodies: next };
      }),
    showAllStructuralBodies: () =>
      set((s) =>
        s.hiddenStructuralBodies.size === 0
          ? s
          : { hiddenStructuralBodies: new Set<number>() },
      ),
    toggleDiagnosisLayer: (id) =>
      set((s) => {
        const next = new Set(s.hiddenDiagnosisLayers);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { hiddenDiagnosisLayers: next };
      }),
    showAllDiagnosisLayers: () =>
      set((s) =>
        s.hiddenDiagnosisLayers.size === 0
          ? s
          : { hiddenDiagnosisLayers: new Set() },
      ),
    setHiddenDiagnosisLayers: (ids) =>
      set({ hiddenDiagnosisLayers: new Set(ids) }),
    setHoveredDiagnosisLayer: (id) => set({ hoveredDiagnosisLayer: id }),
    setHoveredOverlapPair: (key) => set({ hoveredOverlapPair: key }),
    togglePinnedOverlapPair: (key) =>
      set((s) => {
        const unpinning = s.pinnedOverlapPair === key;
        return {
          pinnedOverlapPair: unpinning ? null : key,
          // An isolation with no pair to isolate would leave the viewer
          // showing nothing and no control saying why.
          overlapPairIsolated: unpinning ? false : s.overlapPairIsolated,
        };
      }),
    setOverlapPairIsolated: (v) => set({ overlapPairIsolated: v }),
    setOverlapPairSelected: (key, selected) =>
      set((s) => ({
        overlapPairChoices: withOverlapPairChoice(s.overlapPairChoices, key, {
          selected,
        }),
      })),
    setOverlapPairOp: (key, op) =>
      set((s) => ({
        overlapPairChoices: withOverlapPairChoice(s.overlapPairChoices, key, {
          op,
        }),
      })),
    setOverlapPairCutLoser: (key, bodyIndex) =>
      set((s) => ({
        overlapPairChoices: withOverlapPairChoice(s.overlapPairChoices, key, {
          cutLoserBodyIndex: bodyIndex,
        }),
      })),
    clearOverlapPairChoices: () =>
      set((s) =>
        s.overlapPairChoices.size === 0 && s.hoveredOverlapOutcome === null
          ? s
          : { overlapPairChoices: new Map(), hoveredOverlapOutcome: null },
      ),
    setHoveredOverlapOutcome: (v) =>
      set({ hoveredOverlapOutcome: v, hoveredOverlapPair: v?.key ?? null }),
    setHoveredVersionId: (id) => set({ hoveredVersionId: id }),

    // CAD health UI
    setShowCleaner: (v) => set({ showCleaner: v }),
    setHealMeshLoading: (v) => set({ healMeshLoading: v }),
    setIsAnalyzing: (v) => set({ isAnalyzing: v }),

    // Sidebar panels
    setLeftSidebarOpen: (v) => set({ leftSidebarOpen: v }),
    setChatOpen: (v) => {
      chatPrefs.setOpen(v);
      set({ chatOpen: v });
    },
    setChatApprovalMode: (mode) => set({ chatApprovalMode: mode }),
    setCadSectionOpen: (v) => set({ cadSectionOpen: v }),
    setMeshSectionOpen: (v) => set({ meshSectionOpen: v }),
    setVizSectionOpen: (v) => set({ vizSectionOpen: v }),
    setConsoleOpen: (v) => set({ consoleOpen: v }),
    setSimopsWriteupOpen: (v) => set({ simopsWriteupOpen: v }),

    // Preview
    setPreviewAttemptedForProjectId: (v) =>
      set({ previewAttemptedForProjectId: v }),
    setIsLoadingPreview: (v) => set({ isLoadingPreview: v }),

    // Ansys dropdown
    setIsAnsysDropdownOpen: (v) => set({ isAnsysDropdownOpen: v }),

    // Batch
    setMode: (v) => set({ mode: v }),
    setBatchFiles: (v) => set({ batchFiles: v }),
    setSelectedBatchFileId: (v) => set({ selectedBatchFileId: v }),

    // Session restore
    setIsRestoringSession: (v) => set({ isRestoringSession: v }),
    cancelSessionRestore: () =>
      set({ sessionRestoreCancelled: true, isRestoringSession: false }),
    setRestoredCameraPosition: (v) => set({ restoredCameraPosition: v }),
    setRestoredCameraTarget: (v) => set({ restoredCameraTarget: v }),
    setAuthoringHydratedFor: (v) => set({ authoringHydratedFor: v }),

    // Toast — sonner owns rendering and timing; the action stays on the store
    // so the many existing call sites keep working unchanged.
    showToast: (message: string) => showAppToast(message),

    // Upload and export UI
    setIsUploading: (v) => set({ isUploading: v }),
    setUploadProgress: (v) => set({ uploadProgress: v }),
    setIsExportingAnsys: (v) => set({ isExportingAnsys: v }),

    // Agent chat trigger
    // Mutually exclusive by construction — see the field comments. Clearing
    // the sibling is what a discriminated union would give for free.
    //
    // Only a SET clears it. Both consuming effects call `request*(null)` to
    // eat their own one-shot, and an unconditional clear would make that
    // consume silently discard a sibling request that is merely parked (both
    // park rather than consume while a session restore is in flight) — losing
    // the reader's click with no panel, no toast and nothing to retry. That is
    // the same class of silent drop this exclusivity exists to prevent.
    requestAgentChat: (request) =>
      set(
        request === null
          ? { agentChatRequest: null }
          : {
              agentChatRequest: request,
              agentSessionRequest: null,
            },
      ),
    requestAgentSession: (request) =>
      set(
        request === null
          ? { agentSessionRequest: null }
          : {
              agentSessionRequest: request,
              agentChatRequest: null,
            },
      ),

    // Aggregate setter — defense in depth: zod already validates slicePosition
    // is in [0, 1] for agent input, but a direct call from non-agent code
    // wouldn't go through that gate, so we re-clamp here.
    setViewerSettings: (patch) =>
      set(
        typeof patch.slicePosition === 'number'
          ? { ...patch, slicePosition: clamp01(patch.slicePosition) }
          : patch,
      ),

    // A workspace reset is project-scoped; it must not touch the chat panel or
    // its persisted open/session preference or active approval mode.
    // Preserve the current values (not the frozen boot values in
    // `viewerInitialState`) and leave localStorage alone — cross-user isolation
    // is handled at logout, not here. Approval mode is stored on the server's
    // conversation item; a transcript restore replaces this live value.
    resetViewerState: () =>
      set((state) => ({
        ...viewerInitialState,
        chatOpen: state.chatOpen,
        chatApprovalMode: state.chatApprovalMode,
        isRestoringSession: false,
      })),
    resetJobState: () => set({ ...jobInitialState }),
  };
};
